import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Typography, Button, TextField, Grid, Paper, Stack, Divider,
  IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  Snackbar, Alert, CircularProgress, Chip, Skeleton, MenuItem, Select,
  FormControl, InputLabel, Autocomplete, InputAdornment,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Save as SaveIcon,
  Send as SendIcon,
  CheckCircle as ApproveIcon,
  Cancel as CancelIcon,
  Delete as DeleteIcon,
  FileCopy as DuplicateIcon,
  LibraryBooks as TemplateIcon,
  ThumbDown as RejectIcon,
  Print as PrintIcon,
  History as HistoryIcon,
  PictureAsPdf as PdfIcon,
} from '@mui/icons-material';
import { useHistory, useParams, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import * as actions from '../../redux/actions';
import StatusBadge from '../../components/Documents/StatusBadge';
import DocumentTypeBadge from '../../components/Documents/DocumentTypeBadge';
import LineItemsTable from '../../components/Documents/LineItemsTable';
import SummaryBox from '../../components/Documents/SummaryBox';
import ProjectSelector from '../../components/Documents/ProjectSelector';
import ItemTemplateModal from '../../components/Documents/ItemTemplateModal';
import ChainTimeline from '../../components/Documents/ChainTimeline';
import CreateNextDocModal from '../../components/Documents/CreateNextDocModal';
import QuickCreateModal from '../../components/Documents/QuickCreateModal';
import { generateQuotationPDF } from '../../components/PDF/documentQuotationReport';
import { generateDeliveryNotePDF } from '../../components/PDF/documentDeliveryNoteReport';
import { generateBillingNotePDF } from '../../components/PDF/documentBillingNoteReport';
import { generateReceiptPDF } from '../../components/PDF/documentReceiptReport';
import { generateTaxInvoicePDF } from '../../components/PDF/documentTaxInvoiceReport';

const PDF_GENERATORS = {
  quotation: generateQuotationPDF,
  delivery_note: generateDeliveryNotePDF,
  billing_note: generateBillingNotePDF,
  receipt: generateReceiptPDF,
  tax_invoice: generateTaxInvoicePDF,
};

function generatePDF(doc) {
  const win = window.open('', '_blank');
  const gen = PDF_GENERATORS[doc?.document_type] || generateQuotationPDF;
  return gen(doc, win).catch((err) => {
    if (win && !win.closed) win.close();
    alert(err.message || JSON.stringify(err));
  });
}

function generateRevisionPDF(revSnapshot, doc) {
  const win = window.open('', '_blank');
  const revDoc = {
    ...doc,
    revision: revSnapshot.revision,
    date: revSnapshot.date,
    due_date: revSnapshot.due_date,
    seller: revSnapshot.seller,
    reference_text: revSnapshot.reference_text,
    notes: revSnapshot.notes,
    items: revSnapshot.items,
    subtotal: revSnapshot.subtotal,
    vat_rate: revSnapshot.vat_rate,
    vat_amount: revSnapshot.vat_amount,
    include_vat: revSnapshot.include_vat,
    total: revSnapshot.total,
    total_in_words: revSnapshot.total_in_words,
    snapshot: revSnapshot.snapshot,
  };
  return generateQuotationPDF(revDoc, win).catch((err) => {
    if (win && !win.closed) win.close();
    alert(err.message || JSON.stringify(err));
  });
}

let _uid = Date.now();
const uid = () => ++_uid;

const EMPTY_ITEM = () => ({
  id: uid(),
  description: '',
  description_detail: '',
  quantity: 1,
  unit: 'JOB',
  unit_price: 0,
  amount: 0,
});

const DOC_TYPE_LABEL = {
  quotation: 'ใบเสนอราคา',
  delivery_note: 'ใบส่งงาน',
  billing_note: 'ใบวางบิล',
  receipt: 'ใบเสร็จรับเงิน',
  tax_invoice: 'ใบกำกับภาษี',
};

const INIT_FORM = {
  document_number: '',
  date: dayjs().format('YYYY-MM-DD'),
  due_date: dayjs().format('YYYY-MM-DD'),
  project_id: null,
  customer_name_override: '',
  customer_address_override: '',
  customer_tax_id_override: '',
  topic: '',
  contact_name: '',
  contact_email: '',
  contact_phone: '',
  seller: '',
  customer_po_number: '',
  reference_text: '',
  notes: '',
  payment_terms: '',
  terms_conditions: '',
  po_document_url: '',
  credit_days: 30,
  internal_notes: '',
  items: [EMPTY_ITEM()],
  discount: 0,
  discountType: '%',
  vat_rate: 7,
  include_vat: false,
};

const EDITABLE_STATUSES = ['draft', 'rejected'];

export default function DocumentDetail() {
  const { id } = useParams();
  const isNew = !id;
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();

  const newDocType = isNew
    ? (new URLSearchParams(location.search).get('type') || 'quotation')
    : null;

  const { rows: projects = [] } = useSelector((s) => s.project);
  const docState = useSelector((s) => s.document);

  const [form, setForm] = useState(INIT_FORM);
  const [doc, setDoc] = useState(null);
  const [chain, setChain] = useState([]);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [snack, setSnack] = useState({ open: false, msg: '', severity: 'success' });
  const [showTemplate, setShowTemplate] = useState(false);
  const [nextDocModal, setNextDocModal] = useState({ open: false, targetType: null });
  const [quickModal, setQuickModal] = useState({ open: false, targetType: null, parentId: null });
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [previewingNumber, setPreviewingNumber] = useState(false);
  const [numberEdited, setNumberEdited] = useState(false);
  const [numberConflict, setNumberConflict] = useState({ open: false, original: '', suggested: '', input: '', error: '' });
  const [uploadingPO, setUploadingPO] = useState(false);
  const poInputRef = React.useRef(null);

  const isEditable = isNew || (doc && EDITABLE_STATUSES.includes(doc.status));

  // Load project list — ใช้ lite=true เพื่อ skip heavy aggregate (timestamps/expenses/budget)
  // ลดเวลา query จาก ~2400ms → ~100ms สำหรับ dropdown
  useEffect(() => {
    dispatch(actions.projectAll({ size: 9999, lite: true }));
  }, [dispatch]);

  // Preview document number สำหรับเอกสารใหม่
  useEffect(() => {
    if (!isNew) return;
    setPreviewingNumber(true);
    setNumberEdited(false);
    dispatch(actions.documentPreviewNumber(newDocType)).then((num) => {
      if (num) setField('document_number', num);
    }).finally(() => setPreviewingNumber(false));
  }, [isNew, newDocType, dispatch]);

  // Auto-fill reference_text ตาม doc type และ project ที่เลือก
  const PARENT_TYPE = { delivery_note: 'quotation', billing_note: 'delivery_note', receipt: 'billing_note', tax_invoice: 'receipt' };
  useEffect(() => {
    if (!isNew || !form.project_id) return;
    if (newDocType === 'quotation') {
      setField('reference_text', 'Rev 01');
      return;
    }
    const parentType = PARENT_TYPE[newDocType];
    if (!parentType) return;
    const projectId = form.project_id?._id || form.project_id;
    dispatch(actions.documentFindLatest({ type: parentType, project_id: projectId })).then((docs) => {
      const latest = docs[0];
      if (!latest) return;
      const po = form.customer_po_number;
      const ref = (newDocType === 'delivery_note' && po)
        ? `${latest.document_number}_PO_${po}`
        : latest.document_number;
      setForm((prev) => ({
        ...prev,
        reference_text: ref,
        notes: latest.notes || prev.notes,
        terms_conditions: latest.terms_conditions || prev.terms_conditions,
        payment_terms: latest.payment_terms || prev.payment_terms,
      }));
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNew, form.project_id, newDocType, dispatch]);

  // Load document if editing
  const loadDoc = useCallback(async () => {
    if (isNew) return;
    setLoading(true);
    try {
      const data = await dispatch(actions.documentGet(id));
      if (data) {
        setDoc(data);
        hydrateForm(data);
      }
    } catch {
      showSnack('ไม่พบเอกสาร', 'error');
    } finally {
      setLoading(false);
    }
  }, [id, isNew, dispatch]);

  // Load chain
  const loadChain = useCallback(async () => {
    if (isNew) return;
    try {
      const data = await dispatch(actions.documentGetChain(id));
      if (data) setChain(data);
    } catch {
      // chain load failure is non-critical
    }
  }, [id, isNew, dispatch]);

  useEffect(() => {
    loadDoc();
    loadChain();
  }, [loadDoc, loadChain]);

  // Re-hydrate form เมื่อ projects โหลดเสร็จหลังจาก document (race condition fix)
  const docRef = useRef(null);
  useEffect(() => {
    if (doc) docRef.current = doc;
  }, [doc]);
  useEffect(() => {
    if (docRef.current && projects.length > 0) {
      hydrateForm(docRef.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projects]);

  function hydrateForm(d) {
    const proj = projects.find((p) => p._id === (d.project_id?._id || d.project_id));
    setForm({
      date: d.date ? dayjs(d.date).format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD'),
      due_date: d.due_date ? dayjs(d.due_date).format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD'),
      project_id: proj || d.project_id || null,
      customer_name_override: d.snapshot?.customer_name || '',
      customer_address_override: d.snapshot?.customer_address || '',
      customer_tax_id_override: d.snapshot?.customer_tax_id || '',
      topic: d.topic || d.snapshot?.project_name || '',
      contact_name: d.snapshot?.contact_name || '',
      contact_email: d.snapshot?.contact_email || '',
      contact_phone: d.snapshot?.contact_phone || '',
      seller: d.seller || 'SOTUS Engineering Co., Ltd.',
      customer_po_number: d.customer_po_number || '',
      reference_text: d.reference_text || '',
      notes: d.notes || '',
      payment_terms: d.payment_terms || '',
      terms_conditions: d.terms_conditions || '',
      po_document_url: d.po_document_url || '',
      credit_days: d.snapshot?.credit_days || 30,
      internal_notes: d.internal_notes || '',
      items: d.items?.length > 0
        ? d.items.map((it) => ({ ...it, id: it._id || uid() }))
        : [EMPTY_ITEM()],
      discount: d.discount || 0,
      discountType: d.discountType || '%',
      vat_rate: d.vat_rate ?? 7,
      include_vat: d.include_vat ?? false,
    });
  }

  const setField = (key, val) => setForm((p) => ({ ...p, [key]: val }));

  const handleUploadPO = async (file) => {
    if (!file) return;
    setUploadingPO(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await api.post(
        `${process.env.REACT_APP_API_URL}/upload/file`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      setField('po_document_url', data?.url || '');
      setErrors((e) => ({ ...e, po_document_url: undefined }));
    } catch {
      showSnack('อัพโหลดไฟล์ไม่สำเร็จ — ไฟล์ต้องไม่เกิน 1MB', 'error');
    } finally {
      setUploadingPO(false);
    }
  };

  // Stable callback สำหรับ LineItemsTable — ป้องกัน focus-loss bug
  const handleItemsChange = useCallback((items) => setForm((p) => ({ ...p, items })), []);

  // Merge 8 setState calls เป็น 1 ครั้ง — field names ตรงกับ Contact model
  const handleProjectChange = useCallback((project) => {
    setForm((prev) => {
      if (!project) return { ...prev, project_id: null };
      const c = project.client_id;  // Contact document (populated)
      return {
        ...prev,
        project_id: project,
        // ข้อมูลลูกค้า — ดึงจาก Contact model
        customer_name_override: c?.name || project.name || '',
        customer_address_override:
          [c?.address1, c?.address2, c?.address3, c?.zipcode].filter(Boolean).join(' '),
        customer_tax_id_override: c?.taxId || '',           // Contact.taxId ✓
        // ผู้ติดต่อ — Contact.contact_name (ไม่ใช่ contact_person)
        contact_name: c?.contact_name || '',
        contact_email: c?.email || '',
        contact_phone: c?.mobile || c?.office_phone || '',
        topic: project.name || project.label || '',
        credit_days: c?.credit_days || 30,
      };
    });
  }, []);

  const handleTemplateSelect = (tpl) => {
    const newItem = {
      id: uid(),
      description: tpl.name || '',
      description_detail: tpl.description || '',
      quantity: 1,
      unit: tpl.default_unit || 'JOB',
      unit_price: tpl.default_price || 0,
      amount: tpl.default_price || 0,
    };
    setField('items', [...form.items, newItem]);
    setShowTemplate(false);
  };

  const validate = () => {
    const e = {};
    if (!form.project_id) e.project_id = 'กรุณาเลือกโปรเจกต์';
    if (!form.seller?.trim()) e.seller = 'กรุณาระบุผู้ขาย';
    if ((isNew ? newDocType : doc?.document_type) === 'delivery_note' && !form.po_document_url) {
      e.po_document_url = 'กรุณาแนบใบ PO จากลูกค้าก่อนร่างใบส่งงาน';
    }
    if (!form.items || form.items.length === 0) e.items = 'กรุณาเพิ่มรายการ';
    const blankItem = form.items.some((it) => !it.description?.trim());
    if (blankItem) e.items = 'กรุณาระบุรายละเอียดทุกรายการ';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const buildPayload = () => ({
    document_number: form.document_number?.trim() || undefined,
    date: form.date,
    due_date: form.due_date,
    project_id: form.project_id?._id || form.project_id,
    topic: form.topic,
    seller: form.seller,
    customer_po_number: form.customer_po_number,
    reference_text: form.reference_text,
    notes: form.notes,
    payment_terms: form.payment_terms,
    terms_conditions: form.terms_conditions,
    po_document_url: form.po_document_url,
    internal_notes: form.internal_notes,
    items: form.items.map(({ id: _id, ...it }) => it),
    discount: form.discount,
    discountType: form.discountType,
    vat_rate: form.vat_rate,
    include_vat: form.include_vat,
    snapshot_overrides: {
      customer_name: form.customer_name_override,
      customer_address: form.customer_address_override,
      customer_tax_id: form.customer_tax_id_override,
      contact_name: form.contact_name,
      contact_email: form.contact_email,
      contact_phone: form.contact_phone,
      credit_days: form.credit_days,
    },
  });

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      if (isNew) {
        const created = await dispatch(actions.documentCreate({
          ...buildPayload(),
          document_type: newDocType,
        }));
        showSnack('สร้างเอกสารสำเร็จ');
        if (created?._id) history.replace(`/sale/documents/${created._id}`);
      } else {
        await dispatch(actions.documentUpdate(id, buildPayload()));
        showSnack('บันทึกสำเร็จ');
        loadDoc();
      }
    } catch (err) {
      const suggestedMatch = err.message?.match(/แนะนำเลขถัดไป:\s*(\S+)/);
      const suggestedNum = err.suggested || suggestedMatch?.[1];
      const isConflict = err.code === 'DOCUMENT_NUMBER_CONFLICT' || !!suggestedMatch;
      if (isConflict && suggestedNum) {
        setNumberConflict({ open: true, original: form.document_number, suggested: suggestedNum, input: suggestedNum, error: '' });
      } else {
        showSnack(err.message || 'เกิดข้อผิดพลาด', 'error');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmConflict = async () => {
    const confirmedNumber = numberConflict.input?.trim();
    if (!confirmedNumber) return;
    setNumberConflict((p) => ({ ...p, error: '' }));
    setField('document_number', confirmedNumber);
    setNumberEdited(true);
    setSaving(true);
    try {
      const created = await dispatch(actions.documentCreate({
        ...buildPayload(),
        document_number: confirmedNumber,
        document_type: newDocType,
      }));
      setNumberConflict({ open: false, original: '', suggested: '', input: '', error: '' });
      showSnack('สร้างเอกสารสำเร็จ');
      if (created?._id) history.replace(`/sale/documents/${created._id}`);
    } catch (err) {
      const suggestedMatch = err.message?.match(/แนะนำเลขถัดไป:\s*(\S+)/);
      const suggestedNum = err.suggested || suggestedMatch?.[1];
      const isConflict = err.code === 'DOCUMENT_NUMBER_CONFLICT' || !!suggestedMatch;
      if (isConflict && suggestedNum) {
        setNumberConflict((p) => ({
          ...p,
          error: `เลข "${confirmedNumber}" ถูกใช้ไปแล้ว — แนะนำ: ${suggestedNum}`,
          suggested: suggestedNum,
        }));
      } else {
        setNumberConflict((p) => ({ ...p, open: false }));
        showSnack(err.message || 'เกิดข้อผิดพลาด', 'error');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleChangeStatus = async (newStatus) => {
    setSaving(true);
    try {
      await dispatch(actions.documentChangeStatus(id, newStatus));
      showSnack(`เปลี่ยนสถานะเป็น "${newStatus}" สำเร็จ`);
      loadDoc();
      loadChain();
    } catch (err) {
      showSnack(err.message || 'เกิดข้อผิดพลาด', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setSaving(true);
    try {
      await dispatch(actions.documentDelete(id));
      showSnack('ลบเอกสารสำเร็จ');
      history.replace('/sale/documents');
    } catch (err) {
      showSnack(err.message || 'ลบไม่ได้ — เอกสารนี้อาจมีเอกสารลูก', 'error');
    } finally {
      setSaving(false);
      setDeleteDialog(false);
    }
  };

  const handleDuplicate = async () => {
    setSaving(true);
    try {
      const dup = await dispatch(actions.documentDuplicate(id));
      showSnack('ทำสำเนาสำเร็จ');
      if (dup?._id) history.push(`/sale/documents/${dup._id}`);
    } catch (err) {
      showSnack(err.message || 'เกิดข้อผิดพลาด', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateNext = (targetType, parentDoc) => {
    const parentId = parentDoc?._id || parentDoc?.id || id;
    if (targetType === 'billing_note') {
      setQuickModal({ open: true, targetType: 'billing_note', parentId });
      return;
    }
    if (targetType === 'receipt_invoice') {
      setQuickModal({ open: true, targetType: 'receipt_invoice', parentId });
      return;
    }
    setNextDocModal({ open: true, targetType });
  };

  const handleConfirmQuick = async (formData) => {
    setSaving(true);
    try {
      let created;
      if (quickModal.targetType === 'billing_note') {
        created = await dispatch(actions.documentCreateBilling(quickModal.parentId || id, formData));
        if (created?._id) history.push(`/sale/documents/${created._id}`);
      } else if (quickModal.targetType === 'receipt_invoice') {
        const result = await dispatch(actions.documentCreateReceiptInvoice(quickModal.parentId, formData));
        created = result?.receipt;
        if (created?._id) history.push(`/sale/documents/${created._id}`);
        else loadChain();
      }
      setQuickModal({ open: false, targetType: null, parentId: null });
      showSnack('สร้างเอกสารสำเร็จ');
    } catch (err) {
      showSnack(err.message || 'เกิดข้อผิดพลาด', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmCreateNext = async (formData) => {
    setSaving(true);
    try {
      let created;
      if (nextDocModal.targetType === 'delivery_note') {
        created = await dispatch(actions.documentCreateDelivery(id, formData));
      } else if (nextDocModal.targetType === 'tax_invoice') {
        created = await dispatch(actions.documentCreateInvoice(id, formData));
      }
      setNextDocModal({ open: false, targetType: null });
      showSnack('สร้างเอกสารสำเร็จ');
      if (created?._id) history.push(`/sale/documents/${created._id}`);
    } catch (err) {
      showSnack(err.message || 'เกิดข้อผิดพลาด', 'error');
    } finally {
      setSaving(false);
    }
  };

  const showSnack = (msg, severity = 'success') => {
    setSnack({ open: true, msg, severity });
  };

  if (loading) {
    return (
      <Box sx={{ p: 2 }}>
        <Skeleton variant="rectangular" height={60} sx={{ mb: 2, borderRadius: 2 }} />
        <Skeleton variant="rectangular" height={120} sx={{ mb: 2, borderRadius: 2 }} />
        <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 2 }} />
      </Box>
    );
  }

  const docType = doc?.document_type || 'quotation';
  const isReadOnly = !isEditable;

  return (
    <Box>
      {/* Top bar */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }} flexWrap="wrap" gap={1}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <IconButton onClick={() => history.push('/sale/documents')} size="small">
            <BackIcon />
          </IconButton>
          <Typography variant="h6" fontWeight={700}>
            {isNew ? `สร้าง${DOC_TYPE_LABEL[newDocType] || 'เอกสาร'}` : doc?.document_number}
          </Typography>
          {doc && <DocumentTypeBadge type={doc.document_type} />}
          {doc && <StatusBadge status={doc.status} />}
        </Stack>

        <Stack direction="row" spacing={1} flexWrap="wrap">
          {isEditable && (
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
              onClick={handleSave}
              disabled={saving}
              sx={{ borderRadius: 2 }}
            >
              บันทึก
            </Button>
          )}
          {!isNew && (doc?.status === 'draft' || doc?.status === 'rejected') && (
            <Button
              variant="outlined"
              color="info"
              startIcon={<SendIcon />}
              onClick={() => handleChangeStatus('sent')}
              disabled={saving}
              sx={{ borderRadius: 2 }}
            >
              ส่ง
            </Button>
          )}
          {!isNew && doc?.status === 'sent' && (
            <>
              <Button
                variant="outlined"
                color="success"
                startIcon={<ApproveIcon />}
                onClick={() => handleChangeStatus('approved')}
                disabled={saving}
                sx={{ borderRadius: 2 }}
              >
                อนุมัติ
              </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={<RejectIcon />}
                onClick={() => handleChangeStatus('rejected')}
                disabled={saving}
                sx={{ borderRadius: 2 }}
              >
                ปฏิเสธ
              </Button>
            </>
          )}
          {!isNew && (
            <Button
              variant="outlined"
              startIcon={<DuplicateIcon />}
              onClick={handleDuplicate}
              disabled={saving}
              sx={{ borderRadius: 2 }}
            >
              ทำสำเนา
            </Button>
          )}
          {!isNew && doc?.status === 'draft' && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => setDeleteDialog(true)}
              disabled={saving}
              sx={{ borderRadius: 2 }}
            >
              ลบ
            </Button>
          )}
          {!isNew && (
            <Button
              variant="contained"
              color="secondary"
              startIcon={<PrintIcon />}
              onClick={() => generatePDF(doc)}
              disabled={saving}
              sx={{ borderRadius: 2 }}
            >
              พิมพ์ PDF
            </Button>
          )}
        </Stack>
      </Stack>

      {/* Document number banner — แสดงเฉพาะตอนสร้างใหม่ */}
      {isNew && (
        <Paper
          variant="outlined"
          sx={{
            mb: 3, borderRadius: 3, overflow: 'hidden',
            borderColor: 'primary.main', borderWidth: 2,
          }}
        >
          <Box sx={{ px: 3, py: 1.5, bgcolor: 'primary.main' }}>
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.8)', letterSpacing: 1, textTransform: 'uppercase' }}>
              {DOC_TYPE_LABEL[newDocType] || 'เอกสาร'} · เลขที่เอกสาร
            </Typography>
          </Box>
          <Box sx={{ px: 3, py: 2 }}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <TextField
                value={form.document_number}
                onChange={(e) => { setField('document_number', e.target.value); setNumberEdited(true); }}
                placeholder="กำลังโหลด..."
                size="small"
                disabled={previewingNumber}
                InputProps={{
                  startAdornment: previewingNumber
                    ? <InputAdornment position="start"><CircularProgress size={14} /></InputAdornment>
                    : null,
                  sx: { fontFamily: 'monospace', fontWeight: 700, fontSize: 18, letterSpacing: 1.5, borderRadius: 2 },
                }}
                sx={{ minWidth: 260 }}
              />
              <Typography variant="caption" color="text.secondary">
                แก้ไขได้ถ้าต้องการใช้เลขที่ต่างออกไป
              </Typography>
            </Stack>
          </Box>
        </Paper>
      )}

      {/* Chain Timeline */}
      {!isNew && chain.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <ChainTimeline
            chain={chain}
            currentId={id}
            onCreateNext={doc?.status === 'approved' ? handleCreateNext : undefined}
            onPrint={generatePDF}
          />
        </Box>
      )}

      {/* Revision History — quotation only */}
      {!isNew && doc?.document_type === 'quotation' && (
        <Box sx={{ mb: 3 }}>
          <Paper variant="outlined" sx={{ borderRadius: 3, p: 2.5, bgcolor: '#fafafa' }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
              <HistoryIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
              <Typography variant="subtitle2" fontWeight={700} color="text.secondary">
                ประวัติการแก้ไข (Revision History)
              </Typography>
            </Stack>
            <Stack spacing={1}>
              {(doc.revision_snapshots || []).map((snap) => (
                <Stack
                  key={snap.revision}
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ px: 1.5, py: 1, borderRadius: 2, bgcolor: '#fff', border: '1px solid #e0e0e0' }}
                >
                  <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Chip
                      label={`Rev ${String(snap.revision).padStart(2, '0')}`}
                      size="small"
                      sx={{ fontWeight: 700, fontSize: 11, bgcolor: '#FFF3E0', color: '#E65100' }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {snap.saved_at ? dayjs(snap.saved_at).format('DD/MM/YYYY HH:mm') : ''}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {snap.items?.length || 0} รายการ • {(snap.total || 0).toLocaleString('th-TH', { minimumFractionDigits: 2 })} บาท
                    </Typography>
                  </Stack>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<PdfIcon sx={{ fontSize: 13 }} />}
                    onClick={() => generateRevisionPDF(snap, doc)}
                    sx={{ borderRadius: 1.5, fontSize: 11, py: 0.25, px: 1, borderColor: '#F7931E', color: '#F7931E', '&:hover': { bgcolor: '#FFF3E010', borderColor: '#F7931E' } }}
                  >
                    PDF
                  </Button>
                </Stack>
              ))}
              {/* Current revision row */}
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ px: 1.5, py: 1, borderRadius: 2, bgcolor: '#FFF8F0', border: '1.5px solid #F7931E' }}
              >
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <Chip
                    label={`Rev ${String(doc.revision || 1).padStart(2, '0')} (ปัจจุบัน)`}
                    size="small"
                    sx={{ fontWeight: 700, fontSize: 11, bgcolor: '#F7931E', color: '#fff' }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {doc.updatedAt ? dayjs(doc.updatedAt).format('DD/MM/YYYY HH:mm') : ''}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {doc.items?.length || 0} รายการ • {(doc.total || 0).toLocaleString('th-TH', { minimumFractionDigits: 2 })} บาท
                  </Typography>
                </Stack>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<PdfIcon sx={{ fontSize: 13 }} />}
                  onClick={() => generatePDF(doc)}
                  sx={{ borderRadius: 1.5, fontSize: 11, py: 0.25, px: 1, borderColor: '#F7931E', color: '#F7931E', '&:hover': { bgcolor: '#FFF3E010', borderColor: '#F7931E' } }}
                >
                  PDF
                </Button>
              </Stack>
            </Stack>
          </Paper>
        </Box>
      )}

      {/* Form body */}
      <Stack spacing={3}>
        {/* Row 1: Date + Reference info */}
        <Paper variant="outlined" sx={{ borderRadius: 3, p: 3, borderTop: '4px solid #1976d2' }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2, color: 'text.secondary' }}>
            ข้อมูลเอกสาร
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="วันที่ *"
                type="date"
                size="small"
                value={form.date}
                onChange={(e) => setField('date', e.target.value)}
                disabled={isReadOnly}
                InputLabelProps={{ shrink: true }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="ครบกำหนด"
                type="date"
                size="small"
                value={form.due_date}
                onChange={(e) => setField('due_date', e.target.value)}
                disabled={isReadOnly}
                InputLabelProps={{ shrink: true }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            {(docType === 'delivery_note' || docType === 'billing_note' || docType === 'receipt' || docType === 'tax_invoice') && (
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="เลข PO ลูกค้า"
                  size="small"
                  value={form.customer_po_number}
                  onChange={(e) => {
                    const po = e.target.value;
                    setForm((prev) => {
                      const base = prev.reference_text?.split('_PO_')[0] || prev.reference_text || '';
                      return {
                        ...prev,
                        customer_po_number: po,
                        reference_text: base ? (po ? `${base}_PO_${po}` : base) : prev.reference_text,
                      };
                    });
                  }}
                  disabled={isReadOnly}
                  placeholder="เช่น PO_106/2569"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
            )}
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="เลขที่อ้างอิง"
                size="small"
                value={form.reference_text}
                onChange={(e) => setField('reference_text', e.target.value)}
                disabled={isReadOnly}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Row 2: Customer + Job info */}
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper variant="outlined" sx={{ borderRadius: 3, p: 3, height: '100%', borderTop: '4px solid #ed6c02' }}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2, color: 'text.secondary' }}>
                ข้อมูลลูกค้า
              </Typography>
              <Stack spacing={2}>
                <ProjectSelector
                  projects={projects}
                  value={form.project_id}
                  onChange={handleProjectChange}
                  error={!!errors.project_id}
                  helperText={errors.project_id}
                  disabled={isReadOnly || !isNew}
                />
                <TextField
                  fullWidth
                  label="ชื่อลูกค้า"
                  size="small"
                  value={form.customer_name_override}
                  onChange={(e) => setField('customer_name_override', e.target.value)}
                  disabled={isReadOnly}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
                <TextField
                  fullWidth
                  label="ที่อยู่ลูกค้า"
                  size="small"
                  multiline
                  rows={2}
                  value={form.customer_address_override}
                  onChange={(e) => setField('customer_address_override', e.target.value)}
                  disabled={isReadOnly}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
                <TextField
                  fullWidth
                  label="เลขผู้เสียภาษี"
                  size="small"
                  value={form.customer_tax_id_override}
                  onChange={(e) => setField('customer_tax_id_override', e.target.value)}
                  disabled={isReadOnly}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Stack>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper variant="outlined" sx={{ borderRadius: 3, p: 3, height: '100%', borderTop: '4px solid #ed6c02' }}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2, color: 'text.secondary' }}>
                รายละเอียดงาน
              </Typography>
              <Stack spacing={2}>
                <TextField
                  fullWidth
                  label="ชื่องาน / หัวข้อ"
                  size="small"
                  multiline
                  rows={2}
                  value={form.topic}
                  onChange={(e) => setField('topic', e.target.value)}
                  disabled={isReadOnly}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
                <TextField
                  fullWidth
                  label="ผู้ติดต่อ"
                  size="small"
                  value={form.contact_name}
                  onChange={(e) => setField('contact_name', e.target.value)}
                  disabled={isReadOnly}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
                <TextField
                  fullWidth
                  label="อีเมลผู้ติดต่อ"
                  size="small"
                  value={form.contact_email}
                  onChange={(e) => setField('contact_email', e.target.value)}
                  disabled={isReadOnly}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
                <TextField
                  fullWidth
                  label="ผู้ขาย *"
                  size="small"
                  value={form.seller}
                  onChange={(e) => setField('seller', e.target.value)}
                  disabled={isReadOnly}
                  error={!!errors.seller}
                  helperText={errors.seller}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Stack>
            </Paper>
          </Grid>
        </Grid>

        {/* PO Attachment — delivery_note only */}
        {(isNew ? newDocType : doc?.document_type) === 'delivery_note' && (
          <Paper
            variant="outlined"
            sx={{
              borderRadius: 3, p: 3,
              borderColor: errors.po_document_url ? 'error.main' : 'divider',
              borderWidth: errors.po_document_url ? 2 : 1,
            }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
              <Stack direction="row" alignItems="center" gap={1}>
                <Typography variant="subtitle2" fontWeight={700}>แนบใบ PO จากลูกค้า</Typography>
                <Chip label="บังคับ" size="small" color="error" sx={{ height: 18, fontSize: 10 }} />
              </Stack>
              {isEditable && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={uploadingPO ? <CircularProgress size={13} /> : null}
                  onClick={() => poInputRef.current?.click()}
                  disabled={uploadingPO}
                  sx={{ borderRadius: 2, fontSize: 12 }}
                >
                  {uploadingPO ? 'กำลังอัพโหลด...' : 'เลือกไฟล์'}
                </Button>
              )}
              <input
                ref={poInputRef}
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                style={{ display: 'none' }}
                onChange={(e) => handleUploadPO(e.target.files?.[0])}
              />
            </Stack>
            {form.po_document_url ? (
              <Stack direction="row" alignItems="center" gap={1}>
                <Chip
                  label="ดูใบ PO"
                  size="small"
                  onClick={() => window.open(form.po_document_url, '_blank')}
                  sx={{ bgcolor: '#dcfce7', color: '#15803d', fontWeight: 700, cursor: 'pointer', border: '1px solid #bbf7d0' }}
                />
                <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 400 }}>
                  {form.po_document_url.split('/').pop()}
                </Typography>
                {isEditable && (
                  <Button size="small" color="error" sx={{ fontSize: 11, minWidth: 0, p: 0.5 }} onClick={() => setField('po_document_url', '')}>
                    ลบ
                  </Button>
                )}
              </Stack>
            ) : (
              <Typography variant="caption" color={errors.po_document_url ? 'error' : 'text.disabled'}>
                {errors.po_document_url || 'ยังไม่ได้แนบไฟล์ — รองรับ PDF, JPG, PNG (สูงสุด 1MB)'}
              </Typography>
            )}
          </Paper>
        )}

        {/* Row 3: Items */}
        <Paper variant="outlined" sx={{ borderRadius: 3, p: 3 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" fontWeight={700}>รายการสินค้า / บริการ</Typography>
            {isEditable && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<TemplateIcon />}
                onClick={() => setShowTemplate(true)}
                sx={{ borderRadius: 2, fontSize: 12 }}
              >
                เพิ่มจากแม่แบบ
              </Button>
            )}
          </Stack>
          <LineItemsTable
            items={form.items}
            onChange={handleItemsChange}
            errors={errors}
            disabled={isReadOnly}
          />
        </Paper>

        {/* Row 4: Notes + Summary */}
        <Grid container spacing={3}>
          <Grid item xs={12} md={7}>
            <Paper variant="outlined" sx={{ borderRadius: 3, p: 3 }}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2 }}>หมายเหตุ</Typography>
              <Stack spacing={2}>
                <TextField
                  fullWidth
                  label="หมายเหตุ (แสดงในเอกสาร)"
                  multiline
                  rows={3}
                  size="small"
                  value={form.notes}
                  onChange={(e) => setField('notes', e.target.value)}
                  disabled={isReadOnly}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
                <TextField
                  fullWidth
                  label="เงื่อนไขการจ่ายเงิน (แสดงในเอกสาร)"
                  size="small"
                  value={form.payment_terms}
                  onChange={(e) => setField('payment_terms', e.target.value)}
                  disabled={isReadOnly}
                  placeholder="เช่น โอนเงินภายใน 30 วัน / เงินสด"
                  helperText={isReadOnly ? '' : 'ถ้าว่างไว้ — จะไม่แสดงในเอกสาร'}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
                {docType === 'quotation' && (
                  <TextField
                    fullWidth
                    label="เงื่อนไขการเสนอราคา (แต่ละบรรทัด = 1 ข้อ)"
                    multiline
                    rows={4}
                    size="small"
                    value={form.terms_conditions}
                    onChange={(e) => setField('terms_conditions', e.target.value)}
                    disabled={isReadOnly}
                    placeholder={'Work Period : 30 Days\nWarranty : 6 Month'}
                    helperText={isReadOnly ? '' : 'ถ้าว่างไว้ — ส่วนเงื่อนไขจะไม่แสดงใน PDF'}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                )}
                <TextField
                  fullWidth
                  label="บันทึกภายใน"
                  multiline
                  rows={2}
                  size="small"
                  value={form.internal_notes}
                  onChange={(e) => setField('internal_notes', e.target.value)}
                  disabled={isReadOnly}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Stack>
            </Paper>
          </Grid>
          <Grid item xs={12} md={5}>
            <Paper variant="outlined" sx={{ borderRadius: 3, p: 3 }}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2 }}>สรุปยอดเงิน</Typography>
              <SummaryBox
                form={form}
                onChange={isEditable ? setField : () => {}}
              />
            </Paper>
          </Grid>
        </Grid>
      </Stack>

      {/* Modals */}
      <ItemTemplateModal
        open={showTemplate}
        onClose={() => setShowTemplate(false)}
        onSelect={handleTemplateSelect}
      />

      <CreateNextDocModal
        open={nextDocModal.open}
        onClose={() => setNextDocModal({ open: false, targetType: null })}
        parentDoc={doc}
        targetType={nextDocModal.targetType}
        onConfirm={handleConfirmCreateNext}
        loading={saving}
      />

      <QuickCreateModal
        open={quickModal.open}
        onClose={() => setQuickModal({ open: false, targetType: null, parentId: null })}
        targetType={quickModal.targetType}
        parentNotes={doc?.notes || ''}
        parentTermsConditions={doc?.terms_conditions || ''}
        parentPaymentTerms={doc?.payment_terms || ''}
        referenceText={
          quickModal.targetType === 'billing_note'
            ? chain.find((d) => d.document_type === 'quotation')?.document_number || ''
            : quickModal.targetType === 'receipt_invoice'
            ? chain.find((d) => (d._id === quickModal.parentId || d.id === quickModal.parentId))?.document_number
              || chain.find((d) => d.document_type === 'billing_note')?.document_number || ''
            : ''
        }
        onConfirm={handleConfirmQuick}
        loading={saving}
      />

      {/* Document number conflict dialog */}
      <Dialog
        open={numberConflict.open}
        onClose={() => setNumberConflict((p) => ({ ...p, open: false }))}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Box sx={{ width: 32, height: 32, borderRadius: 2, bgcolor: '#fef3c7', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Typography sx={{ fontSize: 16 }}>⚠️</Typography>
            </Box>
            <Typography fontWeight={700}>เลขที่เอกสารซ้ำ</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            เลขที่ <b style={{ color: '#dc2626' }}>{numberConflict.original}</b> ถูกใช้ไปแล้ว
            ระบบแนะนำเลขถัดไปด้านล่าง — แก้ไขได้ก่อนกดยืนยัน
          </Typography>
          <TextField
            fullWidth
            label="เลขที่เอกสารใหม่"
            value={numberConflict.input}
            onChange={(e) => setNumberConflict((p) => ({ ...p, input: e.target.value, error: '' }))}
            autoFocus
            size="small"
            error={!!numberConflict.error}
            helperText={numberConflict.error || ' '}
            InputProps={{ sx: { fontFamily: 'monospace', fontWeight: 700, fontSize: 15, letterSpacing: 1 } }}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />
          {numberConflict.error && numberConflict.suggested && (
            <Button
              size="small"
              variant="text"
              sx={{ mt: -1, fontSize: 12, p: 0, color: 'primary.main' }}
              onClick={() => setNumberConflict((p) => ({ ...p, input: p.suggested, error: '' }))}
            >
              ใช้เลข {numberConflict.suggested} แทน
            </Button>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            onClick={() => setNumberConflict((p) => ({ ...p, open: false }))}
            variant="outlined"
            sx={{ borderRadius: 2 }}
          >
            ยกเลิก
          </Button>
          <Button
            onClick={handleConfirmConflict}
            variant="contained"
            disabled={saving || !numberConflict.input?.trim()}
            sx={{ borderRadius: 2 }}
          >
            {saving ? 'กำลังสร้าง...' : '✅ ยืนยันและสร้าง'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete dialog */}
      <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle fontWeight={700}>ยืนยันการลบ</DialogTitle>
        <DialogContent>
          <Typography>
            ต้องการลบเอกสาร <b>{doc?.document_number}</b> ใช่หรือไม่?
            การดำเนินการนี้ไม่สามารถย้อนกลับได้
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteDialog(false)} variant="outlined" sx={{ borderRadius: 2 }}>
            ยกเลิก
          </Button>
          <Button onClick={handleDelete} variant="contained" color="error" sx={{ borderRadius: 2 }} disabled={saving}>
            ลบ
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack((p) => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snack.severity} onClose={() => setSnack((p) => ({ ...p, open: false }))}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
